import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import InvoiceModal from './InvoiceModal';
import { invoiceService } from '../services/invoiceService';
import { jobService } from '../services/jobService';

const SortIcon = ({ column, sortConfig }) => {
  const active = sortConfig.key === column;
  const asc = active && sortConfig.dir === 'asc';
  const desc = active && sortConfig.dir === 'desc';
  return (
    <span className="inline-flex flex-col ml-1 align-middle" style={{ lineHeight: 1 }}>
      <svg width="8" height="5" viewBox="0 0 8 5" className={asc ? 'text-blue-500' : 'text-gray-300 dark:text-gray-600'}><path d="M4 0L8 5H0z" fill="currentColor"/></svg>
      <svg width="8" height="5" viewBox="0 0 8 5" className={desc ? 'text-blue-500' : 'text-gray-300 dark:text-gray-600'} style={{ marginTop: 2 }}><path d="M4 5L0 0H8z" fill="currentColor"/></svg>
    </span>
  );
};

const STATUS_STYLES = {
  'Not Sent':       'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  'Sent':           'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  'Paid':           'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  'Partially Paid': 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
  'Overdue':        'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  'Cancelled':      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
};

const StatusBadge = ({ status }) => {
  const cls = STATUS_STYLES[status] || STATUS_STYLES['Not Sent'];
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{status || 'Not Sent'}</span>;
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return `£${parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const calcTotal = (value, vatRate) => {
  const net = parseFloat(value);
  const rate = vatRate ? parseFloat(vatRate.rate) : null;
  if (!rate || isNaN(net)) return null;
  return net * (1 + rate / 100);
};

const calcAmountDue = (inv) => {
  const gross = calcTotal(inv.job?.value, inv.job?.vat_rate);
  const total = gross !== null ? gross : parseFloat(inv.job?.value);
  if (isNaN(total)) return null;
  const paid = inv.amount_paid != null ? parseFloat(inv.amount_paid) : 0;
  return Math.max(0, total - paid);
};

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [markPaidInvoice, setMarkPaidInvoice] = useState(null);
  const [payForm, setPayForm] = useState({ date_paid: '', amount_paid: '' });
  const [payLoading, setPayLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', dir: 'desc' });
  const [activeTab, setActiveTab] = useState('All');

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const data = await invoiceService.getInvoices();
      setInvoices(data.data || []);
      setError('');
    } catch {
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
    jobService.getJobs().then(res => setJobs(res.data || [])).catch(() => setJobs([]));
  }, [fetchInvoices]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleAdd = async (formData) => {
    await invoiceService.createInvoice(formData);
    await fetchInvoices();
    setShowModal(false);
    showSuccess('Invoice created successfully!');
  };

  const handleEdit = async (formData) => {
    await invoiceService.updateInvoice(editingInvoice.id, formData);
    await fetchInvoices();
    setEditingInvoice(null);
    setShowModal(false);
    showSuccess('Invoice updated successfully!');
  };

  const handleDelete = async (id) => {
    try {
      await invoiceService.deleteInvoice(id);
      await fetchInvoices();
      setDeleteConfirm(null);
      showSuccess('Invoice deleted successfully!');
    } catch {
      setError('Failed to delete invoice. Please try again.');
    }
  };

  const handleSend = async (inv) => {
    try {
      const res = await invoiceService.sendInvoice(inv.id);
      await fetchInvoices();
      showSuccess(res.message || `Invoice #${inv.invoice_number} sent.`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send invoice.');
    }
  };

  const openMarkPaid = (inv) => {
    setMarkPaidInvoice(inv);
    const due = calcAmountDue(inv);
    setPayForm({ date_paid: new Date().toISOString().slice(0, 10), amount_paid: due !== null ? due.toFixed(2) : '' });
  };

  const handleMarkPaid = async (e) => {
    e.preventDefault();
    const remainingDue = calcAmountDue(markPaidInvoice);
    const newPayment = parseFloat(payForm.amount_paid);
    const previouslyPaid = markPaidInvoice.amount_paid != null ? parseFloat(markPaidInvoice.amount_paid) : 0;
    const totalPaid = previouslyPaid + newPayment;
    const status = remainingDue !== null && newPayment < remainingDue ? 'Partially Paid' : 'Paid';
    setPayLoading(true);
    try {
      await invoiceService.updateInvoice(markPaidInvoice.id, {
        job_id: markPaidInvoice.job_id,
        status,
        date_paid: payForm.date_paid,
        amount_paid: totalPaid,
      });
      await fetchInvoices();
      showSuccess(`Invoice #${markPaidInvoice.invoice_number} marked as ${status}.`);
      setMarkPaidInvoice(null);
    } catch {
      setError('Failed to update invoice.');
    } finally {
      setPayLoading(false);
    }
  };

  const [noPrereqTarget, setNoPrereqTarget] = useState(null);

  const openEditModal = (inv) => { setEditingInvoice(inv); setShowModal(true); };
  const openAddModal = () => {
    if (jobs !== null && jobs.length === 0) {
      setNoPrereqTarget('job');
      return;
    }
    setEditingInvoice(null);
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditingInvoice(null); };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  };

  const sortedInvoices = [...invoices].sort((a, b) => {
    const { key, dir } = sortConfig;
    let aVal, bVal;
    if (key === 'created_at') {
      aVal = new Date(a.created_at).getTime();
      bVal = new Date(b.created_at).getTime();
    } else if (key === 'invoice_number') {
      aVal = Number(a.invoice_number);
      bVal = Number(b.invoice_number);
    } else if (key === 'job') {
      aVal = (a.job?.title || '').toLowerCase();
      bVal = (b.job?.title || '').toLowerCase();
    } else if (key === 'status') {
      aVal = (a.status || '').toLowerCase();
      bVal = (b.status || '').toLowerCase();
    } else if (key === 'date_paid') {
      aVal = a.date_paid ? new Date(a.date_paid).getTime() : 0;
      bVal = b.date_paid ? new Date(b.date_paid).getTime() : 0;
    } else if (key === 'due_date') {
      aVal = a.due_date ? new Date(a.due_date).getTime() : 0;
      bVal = b.due_date ? new Date(b.due_date).getTime() : 0;
    } else if (key === 'amount_paid') {
      aVal = parseFloat(a.amount_paid) || 0;
      bVal = parseFloat(b.amount_paid) || 0;
    } else if (key === 'amount_due') {
      aVal = calcAmountDue(a) ?? 0;
      bVal = calcAmountDue(b) ?? 0;
    } else {
      aVal = (a[key] || '').toString().toLowerCase();
      bVal = (b[key] || '').toString().toLowerCase();
    }
    if (aVal < bVal) return dir === 'asc' ? -1 : 1;
    if (aVal > bVal) return dir === 'asc' ? 1 : -1;
    return 0;
  });

  const INVOICE_STATUSES = ['Not Sent', 'Sent', 'Paid', 'Partially Paid', 'Overdue', 'Cancelled'];

  const displayedInvoices = activeTab === 'All' ? sortedInvoices : sortedInvoices.filter(inv => (inv.status || 'Not Sent') === activeTab);

  const exportToCSV = () => {
    const headers = ['Invoice #', 'Job', 'Status', 'Due Date', 'Date Paid', 'Value (ex VAT)', 'VAT Rate (%)', 'VAT Amount', 'Total (inc VAT)', 'Amount Paid', 'Amount Due'];
    const rows = displayedInvoices.map((inv) => {
      const net = inv.job?.value != null ? parseFloat(inv.job.value) : null;
      const vatRate = inv.job?.vat_rate ? parseFloat(inv.job.vat_rate.rate) : null;
      const vatAmount = net !== null && vatRate !== null ? net * (vatRate / 100) : null;
      const gross = net !== null && vatAmount !== null ? net + vatAmount : net;
      return [
        inv.invoice_number || '',
        inv.job?.title || '',
        inv.status || 'Not Sent',
        inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-GB') : '',
        inv.date_paid ? new Date(inv.date_paid).toLocaleDateString('en-GB') : '',
        net !== null ? net.toFixed(2) : '',
        vatRate !== null ? vatRate.toFixed(2) : '',
        vatAmount !== null ? vatAmount.toFixed(2) : '',
        gross !== null ? gross.toFixed(2) : '',
        inv.amount_paid != null ? parseFloat(inv.amount_paid).toFixed(2) : '',
        calcAmountDue(inv) !== null ? calcAmountDue(inv).toFixed(2) : '',
      ];
    });

    const escape = (val) => `"${String(val).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((row) => row.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'invoices.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const thClass = 'text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 transition-colors';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-2.5 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm">
              <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Dashboard</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <span className="text-gray-900 dark:text-white font-medium">Invoices</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <ThemeToggle />
              <span className="text-gray-600 dark:text-gray-300 font-medium text-sm sm:text-base order-last sm:order-none">{user?.name}</span>
              <button onClick={logout} className="bg-red-500 text-white border-0 rounded-md px-3 sm:px-4 py-2 text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-red-600">
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-2.5 py-8">
        {successMessage && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-green-700 dark:text-green-300 text-sm font-medium">
            ✓ {successMessage}
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {noPrereqTarget === 'job' && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="text-3xl">⚠️</div>
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-1">No jobs found</h3>
              <p className="text-yellow-700 dark:text-yellow-400 text-sm">
                You need at least one job before creating an invoice. Create a job first.
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setNoPrereqTarget(null)} className="px-3 py-2 border border-yellow-400 text-yellow-700 dark:text-yellow-300 rounded-lg font-semibold text-sm hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors">Dismiss</button>
              <button onClick={() => navigate('/jobs')} className="whitespace-nowrap px-4 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold transition-colors text-sm">Create Job →</button>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Invoice List</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              {activeTab === 'All'
                ? `${invoices.length} ${invoices.length === 1 ? 'invoice' : 'invoices'} total`
                : `${displayedInvoices.length} of ${invoices.length} ${invoices.length === 1 ? 'invoice' : 'invoices'}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {invoices.length > 0 && (
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
              >
                ↓ Export CSV
              </button>
            )}
            <button
              onClick={openAddModal}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity shadow-md"
            >
              <span className="text-lg leading-none">+</span>
              New Invoice
            </button>
          </div>
        </div>

        {/* Status summary */}
        {invoices.length > 0 && (() => {
          const BORDER_COLORS = {
            'Not Sent':       'border-gray-300 dark:border-gray-600',
            'Sent':           'border-blue-300 dark:border-blue-700',
            'Paid':           'border-green-300 dark:border-green-700',
            'Partially Paid': 'border-teal-300 dark:border-teal-700',
            'Overdue':        'border-red-300 dark:border-red-700',
            'Cancelled':      'border-yellow-300 dark:border-yellow-700',
          };
          const summaryStatuses = INVOICE_STATUSES.filter(s =>
            invoices.some(inv => (inv.status || 'Not Sent') === s)
          );
          return (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              {summaryStatuses.map(s => {
                const group = invoices.filter(inv => (inv.status || 'Not Sent') === s);
                const total = group.reduce((sum, inv) => {
                  const gross = calcTotal(inv.job?.value, 0);
                  return sum + (gross !== null ? gross : parseFloat(inv.job?.value || 0));
                }, 0);
                return (
                  <button
                    key={s}
                    onClick={() => setActiveTab(s)}
                    className={`text-left p-3 bg-white dark:bg-gray-800 rounded-xl border-2 ${BORDER_COLORS[s] || BORDER_COLORS['Not Sent']} shadow-sm hover:shadow-md transition-all ${activeTab === s ? 'ring-2 ring-blue-400 dark:ring-blue-500' : ''}`}
                  >
                    <StatusBadge status={s} />
                    <div className="mt-2 text-base font-bold text-gray-900 dark:text-white tabular-nums">{formatValue(total) + " ex VAT"}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{group.length} invoice{group.length !== 1 ? 's' : ''}</div>
                  </button>
                );
              })}
            </div>
          );
        })()}

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Status Tabs */}
          <div className="flex overflow-x-auto border-b border-gray-200 dark:border-gray-700">
            {['All', ...INVOICE_STATUSES].map(tab => {
              const count = tab === 'All' ? invoices.length : invoices.filter(inv => (inv.status || 'Not Sent') === tab).length;
              const active = activeTab === tab;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors -mb-px ${
                    active
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  {tab}
                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                    active ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}>{count}</span>
                </button>
              );
            })}
          </div>
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
                Loading invoices...
              </div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="text-5xl mb-4">🧾</div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No invoices yet</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">Create your first invoice to get started.</p>
              <button onClick={openAddModal} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity">
                <span className="text-lg leading-none">+</span>
                New Invoice
              </button>
            </div>
          ) : displayedInvoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="text-4xl mb-3">🔍</div>
              <p className="text-gray-500 dark:text-gray-400">No <strong>{activeTab}</strong> invoices.</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full whitespace-nowrap">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                      <th className={thClass} onClick={() => handleSort('invoice_number')}>Invoice #<SortIcon column="invoice_number" sortConfig={sortConfig}/></th>
                      <th className={thClass} onClick={() => handleSort('created_at')}>Created<SortIcon column="created_at" sortConfig={sortConfig}/></th>
                      <th className={thClass} onClick={() => handleSort('job')}>Job<SortIcon column="job" sortConfig={sortConfig}/></th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Site</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Value</th>
                      <th className={thClass} onClick={() => handleSort('status')}>Status<SortIcon column="status" sortConfig={sortConfig}/></th>
                      <th className={thClass} onClick={() => handleSort('due_date')}>Due Date<SortIcon column="due_date" sortConfig={sortConfig}/></th>
                      <th className={thClass} onClick={() => handleSort('date_paid')}>Date Paid<SortIcon column="date_paid" sortConfig={sortConfig}/></th>
                      <th className={thClass} onClick={() => handleSort('amount_paid')}>Amount Paid<SortIcon column="amount_paid" sortConfig={sortConfig}/></th>
                      <th className={thClass} onClick={() => handleSort('amount_due')}>Amount Due<SortIcon column="amount_due" sortConfig={sortConfig}/></th>
                      <th className="sticky right-0 text-right px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] dark:shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.2)]" style={{ backgroundColor: '#2b3544' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {displayedInvoices.map((inv) => (
                      <tr key={inv.id} className="text-sm hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-6 py-4 font-mono font-semibold text-gray-900 dark:text-white">#{inv.invoice_number}</td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{formatDate(inv.created_at)}</td>
                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                          {inv.job ? <Link to={`/jobs/${inv.job_id}`} className="text-blue-600 dark:text-blue-400 hover:underline">{inv.job.title}</Link> : '—'}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                          {inv.job?.site ? <Link to={`/sites/${inv.job.site.id}`} className="text-blue-600 dark:text-blue-400 hover:underline">{inv.job.site.name}</Link> : '—'}
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-medium text-gray-900 dark:text-white">{formatValue(inv.job?.value)}</span>
                          {calcTotal(inv.job?.value, inv.job?.vat_rate) !== null && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              Total: {formatValue(calcTotal(inv.job?.value, inv.job?.vat_rate))} ({parseFloat(inv.job?.vat_rate?.rate)}% VAT)
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4"><StatusBadge status={inv.status}/></td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                          {inv.due_date
                            ? new Date(inv.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                          {inv.date_paid
                            ? new Date(inv.date_paid).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                          {inv.amount_paid != null ? formatValue(inv.amount_paid) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 font-semibold">
                          {(() => {
                            const due = calcAmountDue(inv);
                            if (due === null) return <span className="text-gray-400">—</span>;
                            return (
                              <span className={due === 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                                {formatValue(due)}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="sticky right-0 bg-white dark:bg-gray-800 px-6 py-4 text-right shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] dark:shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.2)]">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => window.open(`/invoices/${inv.id}/print`, '_blank')} className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors font-medium">Print</button>
                            {inv.status === 'Not Sent' && (
                              <button onClick={() => handleSend(inv)} className="px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-md transition-colors font-medium">Send</button>
                            )}
                            {!['Paid', 'Cancelled'].includes(inv.status) && (
                              <button onClick={() => openMarkPaid(inv)} className="px-3 py-1.5 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors font-medium">Mark as Paid</button>
                            )}
                            <button onClick={() => openEditModal(inv)} className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors font-medium">Edit</button>
                            <button onClick={() => setDeleteConfirm(inv)} className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors font-medium">Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {displayedInvoices.map((inv) => (
                  <div key={inv.id} className="p-5 space-y-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-mono font-bold text-gray-900 dark:text-white">#{inv.invoice_number}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{formatDate(inv.created_at)}</p>
                      </div>
                      <div className="flex gap-2 ml-2">
                        <button onClick={() => window.open(`/invoices/${inv.id}/print`, '_blank')} className="text-sm text-gray-500 dark:text-gray-400 font-medium">Print</button>
                        {inv.status === 'Not Sent' && (
                          <button onClick={() => handleSend(inv)} className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">Send</button>
                        )}
                        {!['Paid', 'Cancelled'].includes(inv.status) && (
                          <button onClick={() => openMarkPaid(inv)} className="text-sm text-green-600 dark:text-green-400 font-medium">Mark as Paid</button>
                        )}
                        <button onClick={() => openEditModal(inv)} className="text-sm text-blue-600 dark:text-blue-400 font-medium">Edit</button>
                        <button onClick={() => setDeleteConfirm(inv)} className="text-sm text-red-600 dark:text-red-400 font-medium">Delete</button>
                      </div>
                    </div>
                    {inv.job && <p className="text-sm font-medium text-gray-800 dark:text-gray-200"><Link to={`/jobs/${inv.job_id}`} className="text-blue-600 dark:text-blue-400 hover:underline">{inv.job.title}</Link></p>}
                    {inv.job?.site && <p className="text-sm text-blue-600 dark:text-blue-400"><Link to={`/sites/${inv.job.site.id}`} className="hover:underline">{inv.job.site.name}</Link></p>}
                    {inv.job?.value && (
                      <div className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">
                        {formatValue(inv.job.value)}
                        {calcTotal(inv.job.value, inv.job.vat_rate) !== null && (
                          <span className="text-gray-500 ml-1">(Total: {formatValue(calcTotal(inv.job.value, inv.job.vat_rate))})</span>
                        )}
                      </div>
                    )}
                    <div className="pt-1 flex items-center gap-2 flex-wrap">
                      <StatusBadge status={inv.status}/>
                      {inv.date_paid && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Paid: {new Date(inv.date_paid).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                      {inv.amount_paid != null && (
                        <span className="text-xs font-semibold text-green-700 dark:text-green-400">{formatValue(inv.amount_paid)} paid</span>
                      )}
                      {(() => {
                        const due = calcAmountDue(inv);
                        if (due === null || inv.status === 'Cancelled') return null;
                        return (
                          <span className={`text-xs font-semibold ${due === 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {due === 0 ? 'Settled' : `${formatValue(due)} due`}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {showModal && (
        <InvoiceModal
          invoice={editingInvoice}
          onSave={editingInvoice ? handleEdit : handleAdd}
          onClose={closeModal}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Invoice</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete invoice <strong>#{deleteConfirm.invoice_number}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteConfirm.id)} className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {markPaidInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Mark as Paid</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">Invoice #{markPaidInvoice.invoice_number}</p>
            {calcAmountDue(markPaidInvoice) !== null && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Remaining balance (inc. VAT): <strong>{formatValue(calcAmountDue(markPaidInvoice))}</strong>
              </p>
            )}
            <form onSubmit={handleMarkPaid} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Date</label>
                <input
                  type="date"
                  required
                  max={new Date().toISOString().slice(0, 10)}
                  value={payForm.date_paid}
                  onChange={e => setPayForm(p => ({ ...p, date_paid: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount Paid inc. VAT (£)</label>
                <input
                  type="number"
                  required
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={payForm.amount_paid}
                  onChange={e => setPayForm(p => ({ ...p, amount_paid: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {payForm.amount_paid && calcAmountDue(markPaidInvoice) !== null && (
                  <p className="text-xs mt-1 font-medium">
                    {parseFloat(payForm.amount_paid) < calcAmountDue(markPaidInvoice)
                      ? <span className="text-amber-600 dark:text-amber-400">Will be marked as Partially Paid</span>
                      : <span className="text-green-600 dark:text-green-400">Will be marked as Paid</span>}
                  </p>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setMarkPaidInvoice(null)}
                  className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={payLoading}
                  className="flex-1 px-4 py-2.5 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors disabled:opacity-50"
                >
                  {payLoading ? 'Saving…' : 'Confirm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
